import {
  API_CONFIG,
  KycStatus,
  LiveAccountRegistration,
  LiveAccountRegistrationData,
  Page,
  TradingAccountMigrationStatus,
} from '@msrllabs/pt-api';

import { emptyPage, fetchJson, fetchResponse } from './apiUtils';

export type OnboardingProgressFileMetadata = {
  fileId: string;
  purpose: string;
  originalFilename: string;
  contentType: string;
  size: number;
  uploadedAt: string;
};

export type OnboardingProgressData = {
  id?: string;
  version?: number;
  cognitoId: string;
  kycFormVersion: string;
  resumeStep?: number;
  resumeRoute?: string;
  data: Record<string, unknown>;
  files?: OnboardingProgressFileMetadata[];
  createdAt?: string;
  updatedAt?: string;
};

export const getTAndCByAccountType = async (
  _accountType: string,
  _token?: string | Promise<string>
): Promise<Page<{ accountType: string; id: number; title: string; url: string }>> => {
  return emptyPage();
};

export const getKycConsentCatalog = (token?: string | Promise<string>) => {
  return fetchJson<Record<string, string>>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/liveAccountRegistrations/consents`,
    undefined,
    token ?? API_CONFIG.accessToken
  );
};

export const getMigrationStatus = (fromAccountType: string, toAccountType: string, token?: string | Promise<string>) => {
  return fetchJson<{ status: TradingAccountMigrationStatus; reason: string }>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/trading_disp/v1/migrations/${fromAccountType}/${toAccountType}/status`,
    undefined,
    token ?? API_CONFIG.accessToken
  );
};

export const getKycStatus = async (cognitoId: string, token?: string | Promise<string>) => {
  const response = await fetchJson<{ status: KycStatus }>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/cognitoUsers/${cognitoId}/liveAccountRegistrationStatus`,
    undefined,
    token ?? API_CONFIG.accessToken
  );
  return response.status;
};

export const getOnboardingProgress = async (cognitoId: string, token?: string | Promise<string>) => {
  return fetchJson<OnboardingProgressData | null>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/cognitoUsers/${cognitoId}/onboardingProgress`,
    undefined,
    token ?? API_CONFIG.accessToken
  );
};

export const updateOnboardingProgress = async (
  cognitoId: string,
  progress: OnboardingProgressData,
  token?: string | Promise<string>
) => {
  return fetchJson<OnboardingProgressData>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/cognitoUsers/${cognitoId}/onboardingProgress`,
    {
      body: JSON.stringify(progress),
      headers: { 'Content-Type': 'application/json' },
      method: 'PUT',
    },
    token ?? API_CONFIG.accessToken
  );
};

export const deleteOnboardingProgress = async (cognitoId: string, token?: string | Promise<string>) => {
  await fetchResponse(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/cognitoUsers/${cognitoId}/onboardingProgress`,
    await (token ?? API_CONFIG.accessToken),
    {
      method: 'DELETE',
    }
  );
};

export const uploadOnboardingProgressFile = async (
  cognitoId: string,
  purpose: string,
  file: File,
  token?: string | Promise<string>
) => {
  const body = new FormData();
  body.append('purpose', purpose);
  body.append('files', file, file.name);

  return fetchJson<OnboardingProgressData>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/cognitoUsers/${cognitoId}/onboardingProgress/files`,
    {
      body,
      method: 'POST',
    },
    token ?? API_CONFIG.accessToken
  );
};

export const deleteOnboardingProgressFile = async (cognitoId: string, fileId: string, token?: string | Promise<string>) => {
  await fetchResponse(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/cognitoUsers/${cognitoId}/onboardingProgress/files/${fileId}`,
    await (token ?? API_CONFIG.accessToken),
    {
      method: 'DELETE',
    }
  );
};

export const submitKycForm = async (
  formData: LiveAccountRegistrationData,
  submissionId: string,
  token?: string | Promise<string>
) => {
  return fetchJson<LiveAccountRegistration>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/liveAccountRegistrations`,
    {
      body: JSON.stringify({ ...formData, submissionId }),
      headers: { 'Content-Type': 'application/json', 'Idempotency-Key': submissionId },
      method: 'POST',
    },
    token ?? API_CONFIG.accessToken
  );
};

export const uploadImagesForKyc = async (
  liveAccountRegistrationId: string,
  imageUploadData: FormData,
  token?: string | Promise<string>
) => {
  return fetchJson(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/cash/v1/liveAccountRegistrations/${liveAccountRegistrationId}/images`,
    {
      body: imageUploadData,
      method: 'POST',
    },
    token ?? API_CONFIG.accessToken
  );
};

export const migrateAccount = async (fromAccountType: string, toAccountType: string, token?: string | Promise<string>) => {
  return fetchJson<{ message: string; status: string }>(
    `${API_CONFIG.PT_WEB_ENDPOINT}/aqt/trading_disp/v1/migrations/${fromAccountType}/${toAccountType}`,
    {
      method: 'PUT',
    },
    token ?? API_CONFIG.accessToken
  );
};
